import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, Select } from '../../../components/ui/FormField.js';
import type { CreatableBlockKind } from '../types.js';
import { usePopoverShell } from './usePopoverShell.js';

const KIND_LABEL: Record<CreatableBlockKind, string> = {
  manual_block: 'Blocked',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  other: 'Other',
};
const KINDS = Object.keys(KIND_LABEL) as CreatableBlockKind[];

export interface BlockChangePopoverProps {
  /** `move` and `resize` change an existing block; `create` makes a new one from a drawn range. */
  mode: 'move' | 'resize' | 'create';
  carLabel: string;
  /** The existing block's word (move and resize). */
  kindLabel?: string;
  /** The block's current period (move and resize). */
  from?: string;
  /** The proposed period. */
  to: string;
  anchorRect: DOMRect;
  /** Create sends `type` and, only when given, a trimmed `reason`; move and resize send `{}`. */
  onConfirm: (extra: { type?: CreatableBlockKind; reason?: string }) => void;
  onDismiss: () => void;
  error?: string | null;
  pending?: boolean;
}

/**
 * 316 (US4, FR-015) — confirm a block change drawn on the timeline. Nothing is written until
 * Confirm; a refusal stays in the popover. The times come from the drag (redraw to change them).
 */
export function BlockChangePopover({
  mode,
  carLabel,
  kindLabel,
  from,
  to,
  anchorRect,
  onConfirm,
  onDismiss,
  error = null,
  pending = false,
}: BlockChangePopoverProps) {
  const { boxRef, style, placed } = usePopoverShell(anchorRect, onDismiss);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const focusedOnce = useRef(false);
  // FR-025: focus Confirm once the popover is placed and visible (a hidden element cannot take it).
  useEffect(() => {
    if (!placed || focusedOnce.current) return;
    focusedOnce.current = true;
    confirmRef.current?.focus();
  }, [placed]);
  const [type, setType] = useState<CreatableBlockKind>('manual_block');
  const [reason, setReason] = useState('');
  const heading =
    mode === 'create' ? 'New block' : mode === 'resize' ? 'Change length of block' : 'Move block';

  const confirm = () => {
    if (mode !== 'create') return onConfirm({});
    const trimmed = reason.trim();
    onConfirm(trimmed ? { type, reason: trimmed } : { type });
  };

  return createPortal(
    <div
      ref={boxRef}
      data-testid="block-change-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={`${heading} on ${carLabel}`}
      style={style}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink">{heading}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <dl className="mb-2 grid gap-0.5 text-xs">
        {kindLabel && <dd className="font-semibold text-ink">{kindLabel}</dd>}
        <dd className="text-steel">{carLabel}</dd>
        {from && (
          <>
            <dt className="text-steel">From</dt>
            <dd className="text-ink">{from}</dd>
          </>
        )}
        <dt className="text-steel">{from ? 'To' : 'Period'}</dt>
        <dd className="text-ink">{to}</dd>
      </dl>

      {mode === 'create' && (
        <div className="grid gap-2">
          <label htmlFor="block-change-kind" className="flex flex-col gap-1 text-xs text-steel">
            <span>Kind</span>
            <Select
              id="block-change-kind"
              aria-label="Kind"
              value={type}
              onChange={(e) => setType((e.target as HTMLSelectElement).value as CreatableBlockKind)}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </label>
          <label htmlFor="block-change-reason" className="flex flex-col gap-1 text-xs text-steel">
            <span>Reason (optional)</span>
            <Input
              id="block-change-reason"
              aria-label="Reason"
              value={reason}
              onChange={(e) => setReason((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-semantic-danger-text">
          {error}
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-sm text-slate hover:text-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          ref={confirmRef}
          onClick={confirm}
          disabled={pending}
          className="rounded bg-brand-blue px-3 py-1 text-sm text-on-primary disabled:opacity-50"
        >
          {pending ? 'Saving…' : mode === 'create' ? 'Create block' : 'Confirm'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
